import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { createCommunity } from '../lib/communityService';

const CATEGORY_SUGGESTIONS = [
  'Machine Learning', 'Finance & Investing', 'Programming', 'Science',
  'History', 'Health & Medicine', 'Business', 'Mathematics',
  'Physics', 'Philosophy', 'Entrepreneurship', 'Design',
  'Law', 'Economics', 'Environment', 'Psychology',
];

// Try to guess a broad category from a narrow search subject
function guessCategory(subject) {
  if (!subject) return '';
  const s = subject.toLowerCase();
  if (s.includes('machine learning') || s.includes('ai ') || s.includes('neural') || s.includes('deep learning')) return 'Machine Learning';
  if (s.includes('invest') || s.includes('stock') || s.includes('crypto') || s.includes('finance') || s.includes('compound interest') || s.includes('market')) return 'Finance & Investing';
  if (s.includes('code') || s.includes('program') || s.includes('python') || s.includes('javascript') || s.includes('react') || s.includes('software')) return 'Programming';
  if (s.includes('physic') || s.includes('quantum')) return 'Physics';
  if (s.includes('math') || s.includes('calculus') || s.includes('algebra') || s.includes('statistic')) return 'Mathematics';
  if (s.includes('health') || s.includes('medicine') || s.includes('disease') || s.includes('doctor') || s.includes('biology') || s.includes('cancer')) return 'Health & Medicine';
  if (s.includes('history') || s.includes('war') || s.includes('ancient') || s.includes('civiliz')) return 'History';
  if (s.includes('business') || s.includes('startup') || s.includes('entrepreneur') || s.includes('market')) return 'Business';
  if (s.includes('climate') || s.includes('environment') || s.includes('energy') || s.includes('carbon')) return 'Environment';
  if (s.includes('psycholog') || s.includes('brain') || s.includes('behavior') || s.includes('mental')) return 'Psychology';
  if (s.includes('design') || s.includes('ux') || s.includes('ui ') || s.includes('graphic')) return 'Design';
  if (s.includes('law') || s.includes('legal') || s.includes('court') || s.includes('contract')) return 'Law';
  if (s.includes('econom') || s.includes('gdp') || s.includes('inflation')) return 'Economics';
  if (s.includes('philosoph') || s.includes('ethics')) return 'Philosophy';
  // Fallback: title-case the first few words of the subject
  return subject.split(' ').slice(0, 3).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function CreateCommunityModal({ subject, onCreated, onClose }) {
  const { user } = useAuth();

  const initialCategory = guessCategory(subject);

  const [name,          setName]          = useState(initialCategory ? `${initialCategory} Community` : (subject ? `${subject} Community` : ''));
  const [category,      setCategory]      = useState(initialCategory);
  const [desc,          setDesc]          = useState('');
  const [isCourse,      setIsCourse]      = useState(false);
  const [endDate,       setEndDate]       = useState('');
  const [minQuestions,  setMinQuestions]  = useState('');
  const [courseSubject, setCourseSubject] = useState('');
  const [busy,          setBusy]          = useState(false);
  const [error,         setError]         = useState('');

  const pickCategory = (cat) => {
    setCategory(cat);
    setName(prev => {
      if (!prev || prev.endsWith(' Community')) return `${cat} Community`;
      return prev;
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name.trim())     return setError('Please give your community a name.');
    if (!category.trim()) return setError('Please choose a topic / category.');
    if (isCourse && endDate && new Date(endDate) <= new Date()) {
      return setError('End date must be in the future.');
    }
    setBusy(true);
    const { data, error: err } = await createCommunity({
      name:          name.trim(),
      subject:       category.trim(),
      description:   desc.trim(),
      userId:        user.id,
      endDate:       isCourse && endDate ? new Date(endDate).toISOString() : null,
      minQuestions:  isCourse && minQuestions ? parseInt(minQuestions, 10) : null,
      courseSubject: isCourse && courseSubject.trim() ? courseSubject.trim() : null,
    });
    setBusy(false);
    if (err) return setError(err.message);
    onCreated(data);
  };

  // Min date for the end-date picker = tomorrow
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDateStr = tomorrow.toISOString().split('T')[0];

  return (
    <>
      <div className="share-backdrop" onClick={onClose} />
      <div className="share-modal" role="dialog">
        <div className="share-modal-header">
          <h3 className="share-modal-title-text">🌐 Start a Community</h3>
          <button className="share-modal-close" onClick={onClose}>✕</button>
        </div>

        {subject && (
          <div className="comm-subject-tag">
            🔍 Inspired by: <strong>{subject}</strong>
          </div>
        )}

        <form onSubmit={handleCreate} className="comm-create-form">
          {error && <div className="auth-error">{error}</div>}

          {/* ── Topic / Category ── */}
          <div className="auth-field">
            <label>
              Topic / Category
              <span className="comm-cat-hint"> — what broad subject connects this community?</span>
            </label>
            <input
              type="text"
              value={category}
              onChange={e => setCategory(e.target.value)}
              placeholder="e.g. Machine Learning, Finance, History…"
              disabled={busy}
              required
            />
            <div className="comm-cat-chips">
              {CATEGORY_SUGGESTIONS.map(cat => (
                <button
                  key={cat}
                  type="button"
                  className={`comm-cat-chip${category === cat ? ' comm-cat-chip--active' : ''}`}
                  onClick={() => pickCategory(cat)}
                  disabled={busy}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* ── Community Name ── */}
          <div className="auth-field">
            <label>Community Name</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Machine Learning Learners"
              disabled={busy}
              required
            />
          </div>

          {/* ── Description ── */}
          <div className="auth-field">
            <label>Description <span className="auth-optional">(optional)</span></label>
            <textarea
              value={desc}
              onChange={e => setDesc(e.target.value)}
              placeholder="What will members learn and share here?"
              disabled={busy}
              rows={2}
              className="comm-textarea"
            />
          </div>

          {/* ── Course Mode toggle ── */}
          <div className="comm-course-toggle">
            <label className="comm-course-toggle-label">
              <input
                type="checkbox"
                checked={isCourse}
                onChange={e => setIsCourse(e.target.checked)}
                disabled={busy}
              />
              <span>📚 Course Mode</span>
              <span className="comm-course-toggle-hint"> — add an end date, minimum activity, and a course name</span>
            </label>
          </div>

          {/* ── Course fields (shown when Course Mode is on) ── */}
          {isCourse && (
            <div className="comm-course-fields">
              <div className="auth-field">
                <label>Course Subject <span className="auth-optional">(optional)</span></label>
                <input
                  type="text"
                  value={courseSubject}
                  onChange={e => setCourseSubject(e.target.value)}
                  placeholder="e.g. College Admissions, AP Chemistry…"
                  disabled={busy}
                />
              </div>
              <div className="comm-course-row">
                <div className="auth-field">
                  <label>End Date <span className="auth-optional">(optional)</span></label>
                  <input
                    type="date"
                    value={endDate}
                    min={minDateStr}
                    onChange={e => setEndDate(e.target.value)}
                    disabled={busy}
                  />
                </div>
                <div className="auth-field">
                  <label>Min Posts / Student <span className="auth-optional">(optional)</span></label>
                  <input
                    type="number"
                    value={minQuestions}
                    min={1}
                    max={999}
                    onChange={e => setMinQuestions(e.target.value)}
                    placeholder="e.g. 10"
                    disabled={busy}
                  />
                </div>
              </div>
            </div>
          )}

          <button type="submit" className="auth-btn auth-btn--primary" disabled={busy}>
            {busy ? 'Creating…' : isCourse ? '🚀 Create Course Community' : '🚀 Create Community'}
          </button>
        </form>
      </div>
    </>
  );
}

export default CreateCommunityModal;
